import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  ArrowRight,
  Lock,
  Boxes,
  Scale,
  UserRound,
  AlertTriangle,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { Btn, Logo } from "./ui";

const GATE_KEY = "lucidgate_gate_ok";

// Demo walkthrough logins (synthetic tenants / users) used to drop a visitor
// straight into each experience. All accounts are fictional prototype data.
const DEMO = {
  platform: { email: "ops@lucidgate.internal", password: "lucidgate-ops" },
  hrAdmin: { email: "admin@northwind.example", password: "admin123" },
  legalEmployee: { email: "lucas@meridian.example", password: "demo123" },
};

function PasscodeGate({ onPass }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { ok } = await api.gateVerify({ passcode: code });
      if (ok) {
        sessionStorage.setItem(GATE_KEY, "1");
        onPass();
      } else {
        setErr("Incorrect passcode. Please try again.");
      }
    } catch (e) {
      setErr("Could not verify passcode. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lg-backdrop min-h-screen">
      <div className="lg-grain absolute inset-0 opacity-40" />
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
        <div className="lg-rise w-full rounded-2xl border border-subtle bg-surface/90 p-8 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center">
            <Logo className="h-11" />
          </div>
          <h2 className="mt-6 font-display text-xl font-bold">Private preview access</h2>
          <p className="mt-1 text-sm text-slate-400">
            Enter the shared passcode to open the guided overview.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500">
                Passcode
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  data-testid="gate-passcode"
                  type="password"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full rounded-lg border border-subtle bg-elevated py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="gate-error"
                className="rounded-lg border border-block/40 bg-block/10 px-3 py-2 text-sm text-block"
              >
                {err}
              </div>
            )}

            <Btn data-testid="gate-submit" type="submit" disabled={busy} className="w-full">
              {busy ? "Verifying…" : "Unlock overview"} <ArrowRight className="h-4 w-4" />
            </Btn>
          </form>

          <p className="mt-5 text-center font-mono text-[11px] text-slate-600">
            Prototype · all companies &amp; data are fictional / synthetic
          </p>
        </div>
      </div>
    </div>
  );
}

function Tile({ testid, icon: Icon, title, desc, note, busy, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={busy}
      className="group flex h-full flex-col rounded-2xl border border-subtle bg-surface/80 p-6 text-left shadow-xl backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-brand/50 hover:bg-surface disabled:opacity-50"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-brand/40 bg-brand/10">
        <Icon className="h-5 w-5 text-brand" />
      </div>
      <h3 className="mt-5 font-display text-lg font-bold">{title}</h3>
      <p className="mt-2 flex-1 text-sm text-slate-400">{desc}</p>
      <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-slate-600">{note}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
        {busy ? "Opening…" : "Open experience"}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </button>
  );
}

function Overview() {
  const navigate = useNavigate();
  const { login, platformLogin } = useAuth();
  const [tenantList, setTenantList] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.tenants().then(setTenantList).catch(() => setTenantList([]));
  }, []);

  const tenantBySector = (sector) => tenantList.find((t) => t.sector === sector);
  const tenantsLoading = tenantList.length === 0;

  const openPlatform = async () => {
    setErr("");
    setBusy("platform");
    try {
      const data = await api.platformLogin(DEMO.platform);
      platformLogin(data);
      navigate("/platform");
    } catch (e) {
      setErr("Could not open the Platform Overview. Try the internal staff sign-in.");
      navigate("/platform/login");
    } finally {
      setBusy("");
    }
  };

  const openHr = async () => {
    setErr("");
    setBusy("hr");
    try {
      const hr = tenantBySector("hr");
      if (!hr) throw new Error("no-hr");
      const data = await api.login({ tenant_id: hr.id, ...DEMO.hrAdmin });
      login(data);
      navigate("/dashboard");
    } catch (e) {
      setErr("Could not open the HR Dashboard.");
    } finally {
      setBusy("");
    }
  };

  const openEmployee = async () => {
    setErr("");
    setBusy("employee");
    try {
      const legal = tenantBySector("legal");
      if (!legal) throw new Error("no-legal");
      const data = await api.login({ tenant_id: legal.id, ...DEMO.legalEmployee });
      login(data);
      navigate("/my");
    } catch (e) {
      setErr("Could not open the Employee Experience.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="lg-backdrop min-h-screen">
      <div className="lg-grain absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-6xl px-6 py-14">
        <div className="lg-rise flex items-center">
          <Logo className="h-12" />
        </div>
        <h1 className="lg-rise mt-8 max-w-2xl font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
          A guided <span className="text-brand">walkthrough</span> of the control plane.
        </h1>
        <p className="lg-rise mt-4 max-w-xl text-slate-400">
          Pick a perspective below to jump straight in. Each opens a live view using
          synthetic tenants and data — nothing here is real.
        </p>

        {err && (
          <div
            data-testid="overview-error"
            className="mt-6 flex items-center gap-2 rounded-lg border border-block/40 bg-block/10 px-4 py-3 text-sm text-block"
          >
            <AlertTriangle className="h-4 w-4" /> {err}
          </div>
        )}

        <div className="lg-rise mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          <Tile
            testid="tile-platform"
            icon={Boxes}
            title="Platform Overview"
            desc="The internal, cross-tenant staff view — health, request volume and decision trends across every tenant."
            note="Opens as LucidGate staff"
            busy={busy === "platform"}
            onClick={openPlatform}
          />
          <Tile
            testid="tile-hr"
            icon={ShieldCheck}
            title="HR Dashboard"
            desc="Northwind People & Talent admin dashboard, showcasing the Bias Audit View — how often bias-sensitive attributes are caught and blocked."
            note="Opens as Northwind HR admin"
            busy={busy === "hr" || tenantsLoading}
            onClick={openHr}
          />
          <Tile
            testid="tile-employee"
            icon={UserRound}
            title="Employee Experience"
            desc="A regular employee's view at Meridian Legal — personal dashboard plus the Gateway Chat detect → policy → route → audit flow."
            note="Opens as a Meridian Legal employee"
            busy={busy === "employee" || tenantsLoading}
            onClick={openEmployee}
          />
        </div>

        <div className="lg-rise mt-10 flex flex-wrap items-center gap-4">
          <button
            type="button"
            data-testid="overview-lock"
            onClick={() => {
              sessionStorage.removeItem(GATE_KEY);
              window.location.reload();
            }}
            className="font-mono text-[11px] uppercase tracking-widest text-slate-600 transition-colors hover:text-brand"
          >
            <Lock className="mr-1 inline h-3 w-3" /> Lock overview
          </button>
          <a
            href="/login"
            data-testid="overview-signin"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-600 transition-colors hover:text-brand"
          >
            <Scale className="h-3 w-3" /> Standard sign-in →
          </a>
        </div>

        <p className="mt-12 font-mono text-[11px] uppercase tracking-widest text-slate-700">
          Prototype · all companies &amp; data are fictional / synthetic
        </p>
      </div>
    </div>
  );
}

export default function Landing() {
  const [passed, setPassed] = useState(() => sessionStorage.getItem(GATE_KEY) === "1");
  if (!passed) return <PasscodeGate onPass={() => setPassed(true)} />;
  return <Overview />;
}
